// =============================================================================
// Every user-facing string in the app. (§11 — Burmese is a possible later
// addition, and keeping strings in one file is what makes that feasible.)
//
// NOT here: the borrowing-rule failure messages from §6. Those are generated in
// the database, because the database is what enforces the rules — see
// check_member_eligibility() and create_loans(). Translating them later means
// translating them there too. This is a deliberate trade: one source of truth
// for rules beats one source of truth for strings.
// =============================================================================

import { SCHOOL } from '../config/school.js';

export const T = {
  appName: 'Library',

  // Auth
  signIn: 'Sign in',
  signingIn: 'Signing in…',
  email: 'Email',
  password: 'Password',
  signOut: 'Sign out',
  authFailed: 'Email or password not recognised.',
  signInUnreachable:
    'Could not reach the sign-in server. Your password is fine \u2014 the connection is not. '
    + 'Try another network, or switch on the VPN.',
  signInTooMany: 'Too many sign-in attempts. Wait a minute and try again.',
  signInServer: 'The sign-in server is having trouble. Try again shortly.',
  noStaffRecord:
    'This login has no staff record. Ask an admin to set up your account.',

  // Navigation
  navIssue: 'Issue',
  navReturn: 'Return',

  // Issue
  issueTitle: 'Issue books',
  // Staff opened Issue and looked for a scanner. Naming the steps is the whole
  // fix: the search box stops being an optional filter above the real screen
  // and becomes step one of two.
  issueStep1: 'Step 1 of 2 — find the student',
  issueStep1Hint: 'Every loan belongs to a student, so start by finding them. The book scanner appears next.',
  issueStep2: 'Step 2 of 2 — scan the books',
  findMember: 'Find member',
  searchPlaceholder: 'Name, phone, or member code',
  search: 'Search',
  searching: 'Searching…',
  noMembers: 'No members found.',
  changeMember: 'Change member',
  canBorrow: 'Can borrow',
  blocked: 'Cannot borrow',
  slotsUsed: (used, total) => `${used} of ${total} books out`,
  limitReachedHint: (total) =>
    `Limit reached — ${total} book${total === 1 ? '' : 's'}. Remove one from the list to add another.`,
  limitReachedFlash: (total) =>
    `This member may borrow ${total} book${total === 1 ? '' : 's'} at a time. Remove one from the list first.`,
  slotsFull: (used, total) => `${used} of ${total} books out — limit reached`,
  currentLoans: 'Currently borrowed',
  scanOrType: 'Scan or type a book code',
  // Derived so a white-label build shows its own code format, not the one it
  // was copied from.
  bookCodePlaceholder: `e.g. ${SCHOOL.shortCode}-${SCHOOL.exampleBranchCode}-000123`,
  add: 'Add',
  find: 'Find',
  basket: 'Ready to issue',
  basketEmpty: 'No books added yet.',
  remove: 'Remove',
  confirmIssue: (n) => (n === 1 ? 'Issue 1 book' : `Issue ${n} books`),
  confirmIssueEmpty: 'Issue books',
  issuing: 'Issuing…',
  issuedTitle: 'Books issued',
  dueBack: 'Due back',
  issueMore: 'Issue more',
  done: 'Done',

  // Return
  returnTitle: 'Return books',
  scanToReturn: 'Scan or type the book code to return',
  confirmReturn: 'Confirm return',
  returning: 'Returning…',
  returned: 'Returned',
  returnedToday: 'Returned in this session',
  borrowedBy: 'Borrowed by',
  wasDue: 'Was due',
  daysOverdue: (n) => (n === 1 ? '1 day overdue' : `${n} days overdue`),
  onTime: 'On time',
  blockCleared: 'This member can borrow again.',
  returnAnother: 'Return another',

  // Members (§5.5, §7)
  navMembers: 'Members',
  // The phone's fifth tab. Four screens fit across 375px; eight did not.
  navMore: 'More',
  membersTitle: 'Members',
  addMember: 'Add member',
  newMember: 'New member',
  editMember: 'Edit member',
  createMember: 'Create member',
  saveChanges: 'Save changes',
  memberName: 'Full name',
  dateOfBirth: 'Date of birth',
  dobHint: 'Used to suggest books by age band. Optional.',
  branch: 'Branch',
  guardian: 'Parent or guardian',
  guardianName: 'Guardian name',
  guardianPhone: 'Phone',
  guardianPhone2: 'Second phone',
  guardianEmail: 'Email',
  phoneHint: 'Used for overdue reminders. Tap-to-call from the overdue list.',
  notes: 'Notes',
  joined: 'Joined',
  edit: 'Edit',
  backToMembers: '← All members',
  memberNotFound: 'Member not found.',
  filterAll: 'All',
  filterUnpaid: 'Payment pending',
  filterInactive: 'Inactive',
  paymentPending: 'Payment pending',
  inactive: 'Inactive',
  hasOverdue: 'Overdue',
  booksOut: (n) => (n === 1 ? '1 book out' : `${n} books out`),

  // Members table columns (§7)
  colMemberName: 'Name',
  colCode: 'Code',
  colPhone: 'Phone',
  colBooksOut: 'Out',
  colStatus: 'Status',

  // Create-then-charge (§5.5). Creating a member and taking the fee is one job
  // at the desk, so it is one flow: the fee form opens on the new member.
  memberCreatedTakeFee: 'Member created. Record the membership fee now.',
  skipFeeForNow: 'Skip for now',

  // Screens that are reachable on a phone but not workable on one (§2.2).
  desktopOnlyTitle: 'Better on a computer',
  desktopOnlyHint: 'This screen is a wide table. Open it on the desk computer, or turn your phone sideways.',
  desktopOnlyAnyway: 'Show it anyway',

  // Membership fee (§4, §5.5)
  payments: 'Membership fee',
  addPayment: 'Add payment',
  noPayments: 'No payment recorded — this member cannot borrow yet.',
  recordFee: 'Record membership fee',
  receiptHint: 'Copy the number from the paper receipt book. The system records it, it does not issue it.',
  amount: 'Amount',
  paidOn: 'Paid on',
  method: 'Method',
  receiptNo: 'Receipt number',
  savePayment: 'Save payment',
  methods: {
    cash: 'Cash',
    bank_transfer: 'Bank transfer',
    mobile_money: 'Mobile money',
    other: 'Other',
  },

  // Loans on the member screen
  issueToThisMember: 'Issue books',
  returnThis: 'Return',
  returnAll: (n) => `Return all ${n}`,
  returnConfirm: (title) => `Return “${title}” now?`,
  returnAllConfirm: (n) => `Return all ${n} books now?`,
  returnedAll: (n) => (n === 1 ? '1 book returned.' : `${n} books returned.`),
  renew: 'Renew',
  renewing: 'Renewing…',
  renewed: (title) => `${title} renewed.`,
  history: 'Borrowing history',
  borrowedOn: 'Borrowed',
  issuedBy: (name) => `Issued by ${name}`,
  takenBackBy: (name) => `returned to ${name}`,
  recordedBy: (name) => `recorded by ${name}`,
  showCount: (n) => `Show ${n}`,
  hide: 'Hide',
  markedLost: 'Marked lost',

  // Admin (§3)
  adminActions: 'Admin',
  deactivate: 'Mark inactive',
  reactivate: 'Reactivate',
  deactivateHint: 'An inactive member cannot borrow. Their history is kept.',
  reactivateHint: 'This member is inactive and cannot borrow.',
  deactivateReasonPrompt: 'Why is this member being made inactive?',

  // Settings (§7) — admin only
  navSettings: 'Settings',
  settingsTitle: 'Settings',
  adminOnly: 'Admin only',
  adminOnlyHint: 'Ask an admin if you need something changed here.',
  staffAccounts: 'Staff accounts',
  addStaff: 'Add staff',
  newStaff: 'New staff account',
  newStaffHint: 'Creates their login and shows a temporary password to hand over. They can change it later.',
  staffName: 'Full name',
  role: 'Role',
  roleAdmin: 'Admin',
  roleFrontDesk: 'Front desk',
  allBranches: 'All branches',
  thisIsYou: 'This is you',
  createAccount: 'Create account',
  creating: 'Creating…',
  accountCreated: (name) => `Account created for ${name}`,
  passwordShownOnce: 'Write this down now — it is shown once and cannot be recovered. If it is lost, reset the password in Supabase.',
  tempPassword: 'Temporary password',
  copyDetails: 'Copy email and password',
  copied: 'Copied.',
  copyFailed: 'Could not copy — write it down instead.',

  branchSettingsTitle: 'Branch settings',
  borrowingRules: 'Borrowing rules',
  noBranchSettings: 'No branch is assigned to this account.',
  signedInAt: (branch) => `Signed in at ${branch}`,
  signedInAllBranches: 'Signed in across all branches',
  rulesHint: 'Applied at the moment a book is issued. Changing these does not affect books already out.',
  maxLoans: 'Books at once',
  loanPeriod: 'Loan period (days)',
  renewalsAllowed: 'Renewals allowed',
  saveRules: 'Save',
  days: (n) => (n === 1 ? '1 day' : `${n} days`),
  feeChangeHint: 'Changing the fee does not affect payments already recorded.',
  rulesSaved: (branch) => `Rules saved for ${branch}.`,
  feeIs: (amount) => `membership fee ${amount}`,

  // Overdue (§7) — the reminder mechanism
  navOverdue: 'Overdue',
  overdueTitle: 'Overdue books',
  refresh: 'Refresh',
  nothingOverdue: 'Nothing overdue',
  nothingOverdueHint: 'Every book out is still within its loan period.',
  overdueSummary: (books, members) =>
    `${books === 1 ? '1 book' : `${books} books`} overdue, ` +
    `${members === 1 ? '1 member' : `${members} members`} to contact`,
  callGuardian: (phone) => `Call ${phone}`,
  callAlternate: (phone) => `Or call ${phone}`,
  noPhoneOnFile: 'No phone number on file — add one on the member record.',
  dueWas: (date) => `Was due ${date}`,

  // Books / catalogue (§7)
  navBooks: 'Books',
  booksTitle: 'Books',
  searchBooks: 'Search title, author, or category',
  noBooksMatch: 'No books match that search.',
  noBooksYet: 'No books yet. Add the first one.',
  colTitle: 'Title',
  colAuthor: 'Author',
  colAvailability: 'Copies',
  publishedYear: 'Publish year',
  publishedYearOptional: 'e.g. 2018 (optional)',
  publishedPlace: 'Publish place',
  publishedPlaceOptional: 'e.g. UK (optional)',
  published: 'Published',
  bookSaved: 'Book details saved.',
  noteSaved: 'Condition note saved.',
  noteRemoved: 'Condition note removed.',
  conditionNote: 'Condition note',
  conditionNotePlaceholder: 'e.g. Cover torn, still readable',
  noConditionNote: 'No condition note',
  addNote: 'Add note',
  editNote: 'Edit note',
  saveNote: 'Save note',
  colCategory: 'Category',
  colAdded: 'Added',
  borrowHistory: 'Borrowing history',
  noBorrowHistory: 'This book has never been borrowed.',
  historyUnavailable: 'Borrowing history could not be loaded.',
  timesBorrowed: (n) => (n === 1 ? 'Borrowed once' : `Borrowed ${n} times`),
  unknownBorrower: 'Unknown member',
  issuedOn: 'Issued',
  returnedOn: 'Returned',
  renewals: 'Renewals',
  // NOT `issuedBy` — that name is already taken by the sentence form
  // `issuedBy(name)` used on the member screen. These are the <dt> labels for
  // the book history's two-column layout, where the name is the value beside
  // them rather than part of the phrase.
  issuedByLabel: 'Issued by',
  returnedByLabel: 'Returned by',

  colOnLoanTo: 'On loan to',
  colDue: 'Due',
  availableOf: (a, t) => `${a} of ${t} available`,
  overdueSince: (d) => `${d} — overdue`,
  bookDetails: 'Book details',
  copiesHeading: 'Copies',
  outOfService: 'Lost or withdrawn',
  close: 'Close',
  copyStatus: {
    available: 'On shelf',
    on_loan: 'On loan',
    lost: 'Lost',
    damaged: 'Damaged',
    withdrawn: 'Withdrawn',
  },

  // Membership fee setting (§4, 0006)
  membershipFee: 'Membership fee',
  membershipFeeIs: (amount) => `Membership fee: ${amount}`,
  changeFee: 'Change',
  setFee: 'Set membership fee',
  feeNotSet: 'Membership fee not set — no one can join until it is.',
  feeHint: 'Charged once per member. Changing it does not affect fees already recorded.',
  saveFee: 'Save fee',
  feeSaved: (amount) => `Membership fee set to ${amount}.`,

  // Payment kinds (0006)
  paymentKind: 'What is this payment for?',
  kinds: {
    membership: 'Membership fee (one time)',
    late_fee: 'Late fee',
    penalty: 'Penalty',
    other: 'Other',
  },
  membershipAlreadyPaid: 'Membership fee already recorded.',
  kindShort: {
    membership: 'Membership',
    late_fee: 'Late fee',
    penalty: 'Penalty',
    other: 'Other',
  },
  amountFixed: 'Set by the branch membership fee.',
  noteRequired: 'Note (required)',
  noteHint: 'Say what this payment is for — it appears on the member record.',

  // Discounts (0010). The wording follows Happee SMS, which staff already use:
  // a fixed fee they cannot type over, a discount they can, and a net that is
  // arithmetic rather than a second opinion.
  feeCharged: 'Fee',
  discount: 'Discount',
  discountHint: 'Leave at 0 if there is no discount. It cannot be more than the fee.',
  netPayable: 'To collect',
  netFixed: 'Fee minus discount. Paid in full — there are no part payments.',
  paymentReference: 'Payment reference',
  referenceHint: 'The number from the paper receipt book.',
  referenceNotNeeded: 'Not needed — nothing is being collected.',
  bankAccount: 'Which bank account?',
  bankAccountHint: 'Where the transfer landed. Ask if you are not sure.',
  discountedBy: (amount) => `${amount} discount`,

  // Payments module (§7)
  navPayments: 'Payments',
  paymentsTitle: 'Payments',
  noPaymentsMatch: 'No payments in that range.',
  colDate: 'Date',
  colMember: 'Member',
  colFor: 'For',
  colFee: 'Fee',
  colDiscount: 'Discount',
  colCollected: 'Collected',
  colMethod: 'Method',
  colReference: 'Reference',
  colTakenBy: 'Taken by',
  from: 'From',
  to: 'To',
  allKinds: 'All types',
  allMethods: 'All methods',
  searchPayments: 'Member, code, or reference',
  paymentsTotal: (n, collected, discount) =>
    `${n === 1 ? '1 payment' : `${n} payments`} · ${collected} collected · ${discount} discounted`,

  // Edit book (§7 Catalogue). Front desk as well as admin — `titles` is
  // bibliographic, not branch-owned, and 0003 already allowed the write.
  editBook: 'Edit book',
  saveBookChanges: 'Save changes',
  bookUpdated: 'Book updated.',

  // Add books (§5.6, §9.1)
  navAdd: 'Add books',
  addBooksTitle: 'Add books',
  newBook: 'New book',
  bookTitle: 'Title',
  author: 'Author',
  authorOptional: 'Author (optional)',
  category: 'Category',
  categoryNone: 'No category',
  copies: 'How many copies?',
  copiesHint: 'One label is printed per copy.',
  addBook: 'Add book',
  saveBook: 'Save book',
  saving: 'Saving…',
  nothingAddedYet: 'Books you add will appear here, with their labels.',
  addedThisSession: (n) => (n === 1 ? '1 book added' : `${n} books added`),
  addedWithCodes: (title, n) =>
    `Added ${title} — ${n === 1 ? '1 label' : `${n} labels`} ready to print.`,
  printLabels: 'Print labels',
  printAllLabels: 'Print all labels',
  printFiltered: (n) => `Print ${n} shown`,
  filter: 'Filter',
  filterOn: 'Filter (on)',
  clearFilters: 'Clear filters',
  availability: 'Availability',
  allCategories: 'All categories',
  noBooksMatchFilter: 'No books match these filters.',
  showingCount: (shown, total) => (shown === total ? `${total} books` : `${shown} of ${total} books`),
  availabilityOption: {
    all: 'All books',
    onShelf: 'On shelf',
    onLoan: 'On loan',
    overdue: 'Overdue',
    outOfService: 'Lost or withdrawn',
  },
  printSelected: (n) => `Print ${n} selected`,
  selectAllBooks: 'Select all books shown',
  selectBook: (title) => `Select ${title}`,
  preparingLabels: 'Preparing labels…',
  possibleDuplicate: 'This title may already exist',
  duplicateExplain: (title) =>
    `“${title}” looks like something already in the catalogue. Adding copies to the existing title keeps the catalogue tidy; creating a separate title is usually a mistake.`,
  addCopiesToThis: (n) => (n === 1 ? 'Add 1 copy to this' : `Add ${n} copies to this`),
  createSeparateTitle: 'No, create a separate title',

  // Scanner
  scan: 'Scan',
  stopScanning: 'Stop scanning',
  scannerStarting: 'Starting camera…',
  cameraDenied:
    'Camera unavailable. Type the code instead — the field below always works.',
  scannerHint: 'Point the camera at the QR code on the book.',

  mockMode: 'Demo mode — not real data',

  // Generic
  loading: 'Loading…',
  // Shown only once a request has been in flight long enough to look dead
  // (lib/activity.js). Says what is happening AND offers a way out — the
  // previous behaviour was a motionless word and no way to act on it.
  stillWorking: 'This is taking longer than usual.',
  retry: 'Try again',
  cancel: 'Cancel',
  updateAvailable: 'A new version is ready.',
  appVersion: 'App version',
  versionLabel: 'Version',
  checkForUpdates: 'Check for updates',
  checkingForUpdates: 'Checking…',
  upToDate: 'This is the latest version.',
  updateNow: 'Update',
  updating: 'Updating…',
  offline: 'No internet connection. Transactions need connectivity.',
  networkError: 'Could not reach the server. Try again.',
  unexpected: 'Something went wrong. Try again.',
};
